import React, { ReactNode } from "react";
import "../resources/scss/CampaignPanel.scss";

import CustomSkeleton from "./Skeleton/CustomSkeleton";

import { VideoConfig } from "../CampaignPanel";
import { debounce } from "../utils/utils";

interface CampaignPanelVideoProps {
  video: VideoConfig;
  children?: ReactNode;
  onVideoDuration?: (duration: number) => void;
  onIsPlayingChange?: (isPlaying: boolean) => void;
  onVideoEnded?: (e:any) => void;
  allowClose?: boolean;
  onCenterPlayClick?: Function;
  allowSeek?: boolean;
  isMiniPlay?: boolean;
}

const CampaignPanelVideo: React.FC<CampaignPanelVideoProps> = ({
  video,
  children,
  onVideoDuration,
  onIsPlayingChange,
  onVideoEnded,
  allowClose = true,
  onCenterPlayClick=()=>{},
  allowSeek = true,
  isMiniPlay,
}) => {
  const {
    url: videoUrl,
    disableControls = false,
    initialTime = 0,
    width: videoWidth,
    height: videoHeight,
    muted = true,
    onTimeUpdate,
    handleEventTracking,
    noplaybackrate=true,
    disablePictureInPicture=true,
  } = video;
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = React.useState(false);
  const [hasEnded, setHasEnded] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const lastTimeRef = React.useRef(0);

  const debouncedTrackTime = React.useCallback(
    debounce(
      (
        time: number,
        tracker?: (data: any) => void,
        timeUpdater?: (time: number) => void,
      ) => {
        tracker?.({ videoCurrentTime: time });
        timeUpdater?.(time);
      },
      1000,
    ),
    [],
  );

  React.useEffect(() => {
    if (videoRef.current && initialTime > 0) {
      videoRef.current.currentTime = initialTime;
    }
  }, [initialTime]);

  React.useEffect(() => {
    setIsVideoLoaded(false);
    setHasEnded(false);
    setIsPlaying(false);
    lastTimeRef.current = 0;
  }, [videoUrl]);

  const videoElement = (
    <div className="rec_cp_video-container" style={{ position: "relative" }}
    onClick={()=>onCenterPlayClick()}
    >
      {!isVideoLoaded && (
        <div className="rec_cp_skeleton-container" >
          <CustomSkeleton width="100%" height="100%" />
        </div>
      )}
      {isVideoLoaded && !isPlaying && !hasEnded && (
        <button
          className="rec_cp_video-play-button"
          onClick={() => {
            if (videoRef.current) {
              videoRef.current.play().catch(() => {});
            }
          }}
          aria-label="Play"
        >
          <div className={`rec_cp_centerPlay ${isMiniPlay ? "miniPlay" : ""}`}/>
        </button>
      )}
      <video
        ref={videoRef}
        src={videoUrl}
        controls={hasEnded || !allowClose || !disableControls}
        autoPlay={true}
        muted={muted}
        preload="auto"
        playsInline
        className={`rec_cp_video ${!allowClose && !hasEnded ? "rec_cp_video-restricted" : ""}`}
        style={{
          opacity: isVideoLoaded ? 1 : 0,
          width: videoWidth,
          height: videoHeight,
        }}
        onLoadedData={() => {
          setIsVideoLoaded(true);
        }}
        onPlay={() => {
          setIsPlaying(true);
          onIsPlayingChange?.(true);
        }}
        onPause={() => {
          setIsPlaying(false);
          onIsPlayingChange?.(false);
          if (
            !allowClose &&
            !hasEnded &&
            videoRef.current &&
            !videoRef.current.ended &&
            videoRef.current.currentTime < videoRef.current.duration - 0.5
          ) {
            videoRef.current.play().catch(() => {});
          }
        }}
        onSeeking={() => {
          if (!allowSeek && !hasEnded && videoRef.current) {
            if (videoRef.current.currentTime > lastTimeRef.current + 0.5) {
              videoRef.current.currentTime = lastTimeRef.current;
            }
          }
        }}
        onSeeked={() => {
          if (!allowClose && !hasEnded && videoRef.current) {
            if (videoRef.current.currentTime > lastTimeRef.current + 0.5) {
              videoRef.current.currentTime = lastTimeRef.current;
            }
          }
        }}
        onEnded={(e) => {
          setHasEnded(true);
          setIsPlaying(false);
          onIsPlayingChange?.(false);
          if (videoRef.current) {
            videoRef.current.pause();
          }
          onVideoEnded?.(e);
          handleEventTracking?.({ videoEnded: true });
        }}
        onLoadedMetadata={(e) => {
          if (onVideoDuration) {
            onVideoDuration(e.currentTarget.duration * 1000);
          }
          handleEventTracking?.({ videoDuration: e.currentTarget.duration });
        }}
        onTimeUpdate={(e) => {
          const time = e.currentTarget.currentTime;
          if (!allowSeek) {
            // Only update lastTimeRef if it's normal forward playback
            const timeDiff = time - lastTimeRef.current;
            if (timeDiff > 0 && timeDiff < 1.5) {
              lastTimeRef.current = time;
            }
          }
          debouncedTrackTime(time, handleEventTracking, onTimeUpdate);
        }}
        onKeyDown={(e) => {
          if (!allowClose && !hasEnded) {
            const blockedKeys = [
              " ",
              "ArrowRight",
              "ArrowUp",
              "ArrowDown",
              "End",
            ];
            if (blockedKeys.includes(e.key)) {
              e.preventDefault();
            }
          }
        }}
        onContextMenu={(e) => {
          if ((disableControls || !allowClose) && !hasEnded) {
            e.preventDefault();
          }
        }}
        disablePictureInPicture={(disableControls || !allowClose || disablePictureInPicture) && !hasEnded}
        controlsList={
          (disableControls || !allowClose || noplaybackrate) && !hasEnded
            ? "nodownload noplaybackrate"
            : undefined
        }
      >
        Your browser does not support the video tag.
      </video>
    </div>
  );

  const replaceVideoPlaceholder = (
    nodes: ReactNode,
    replacement: ReactNode,
  ): ReactNode => {
    return React.Children.map(nodes, (child) => {
      // Handle string placeholder
      if (typeof child === "string") {
        if (child.includes("{video}")) {
          const parts = child.split("{video}");
          return (
            <>
              {parts.map((part, index) => (
                <React.Fragment key={index}>
                  {part}
                  {index < parts.length - 1 && replacement}
                </React.Fragment>
              ))}
            </>
          );
        }
        return child;
      }

      // Handle recursive traversal for React Elements
      if (React.isValidElement(child)) {
        const childProps = child.props as { children?: ReactNode };
        if (childProps.children) {
          const newChildren = replaceVideoPlaceholder(
            childProps.children,
            replacement,
          );
          return React.cloneElement(child, {
            children: newChildren,
          } as any);
        }
        return child;
      }

      return child;
    });
  };

  if (children) {
    return replaceVideoPlaceholder(
      children,
      videoElement,
    ) as React.ReactElement;
  }

  return videoElement;
};

export default CampaignPanelVideo;
